/**
 * The host every answer composition point mounts. Components only — a file
 * exporting a hook beside one fails the `react-refresh/only-export-components`
 * ratchet, so the twelve-member binding lives in the sibling module.
 */

import { EvidenceAnswerPortsProvider, type EvidenceResponsePorts } from '@heidi/evidence';
import { useMemo, type ReactNode } from 'react';

import { useEvidenceAnswerPortsValue } from './use-evidence-answer-ports';

/**
 * Mount once above any subtree that renders an answer surface. A component
 * reading a port with no host above it throws and names the bundle, because a
 * degraded render would ship a badge with no analytics and no viewer routing
 * and look correct.
 *
 * `responsePorts` is the Chat-owned chrome for `TextResponse`. Hosts that do
 * not render an answer body omit it.
 */
export const EvidenceAnswerPortsHost = ({
  children,
  responsePorts,
}: {
  children: ReactNode;
  responsePorts?: EvidenceResponsePorts;
}): JSX.Element => {
  const ports = useEvidenceAnswerPortsValue();
  const merged = useMemo(
    () => ({ ...ports, response: responsePorts }),
    [ports, responsePorts]
  );
  return (
    <EvidenceAnswerPortsProvider ports={merged}>{children}</EvidenceAnswerPortsProvider>
  );
};
